import * as React from "react";
import { Alert, AlertTitle, Box, Button, Divider, Grid, Stack, Typography } from "@mui/material";
import { ClockCounterClockwiseIcon } from "@phosphor-icons/react/dist/ssr/ClockCounterClockwise";
import { useNavigate } from "react-router-dom";

import type { CampaignRequestDetail } from "@/hooks/api/use-campaign-requests";
import { campaignRequestErrorMessage, useQuoteCampaignRequest } from "@/hooks/api/use-campaign-requests";

import { compareEventOrder, formatDateTime, REQUEST_STATE_LABELS } from "../../constants";
import { humaniseEnum, labelOf } from "../../humanise";
import { CheckMark, EmptyPanel, Fact } from "./parts";
import type { Readiness } from "./readiness";

/**
 * Overview: where this request stands, and who signed what.
 *
 * The one job of this tab is the answer to "can I provision this yet, and if not, what is
 * missing" — the checklist — plus the human record behind it. Everything that used to be stacked
 * around it now lives on its own tab.
 */
export function OverviewTab({
	request,
	readiness,
}: {
	request: CampaignRequestDetail;
	readiness: Readiness;
}): React.JSX.Element {
	return (
		<Stack spacing={3}>
			{request.currentQuote ? null : <NeverPriced request={request} />}

			<Box>
				<Stack spacing={1}>
					{readiness.checks.map((check) => (
						<Stack key={check.key} direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
							<CheckMark tone={check.tone} />
							<Typography variant="body2">{check.label}</Typography>
						</Stack>
					))}
				</Stack>

				<Typography variant="caption" color="text.secondary" sx={{ display: "block", pt: 1.5 }}>
					A warning here is something to have looked at. Only two things gate the Provision button: admin approval of
					the preparation run, and a passed Proving Ground suite — both on Processing &amp; Approval.
				</Typography>
			</Box>

			<Divider />

			<Grid container spacing={3}>
				<Grid size={{ md: 6, xs: 12 }}>
					<Stack spacing={1}>
						<Typography variant="subtitle2">Quote Confirmation</Typography>
						{request.confirmedAt ? (
							<React.Fragment>
								<Fact label="Confirmed by" value={request.confirmedByName ?? "—"} />
								<Fact label="Role" value={humaniseEnum(request.confirmedByRole) || "—"} />
								<Fact label="At" value={formatDateTime(request.confirmedAt)} />
								<Fact label="From IP" value={request.confirmedIp ?? "—"} />
							</React.Fragment>
						) : (
							<Typography variant="body2" color="text.secondary">
								Not confirmed — the rates are not frozen.
							</Typography>
						)}
					</Stack>
				</Grid>

				<Grid size={{ md: 6, xs: 12 }}>
					<Stack spacing={1}>
						<Typography variant="subtitle2">What the Client Confirmed</Typography>
						<Fact
							label="Lead consent confirmed"
							value={request.leadConsentConfirmedAt ? formatDateTime(request.leadConsentConfirmedAt) : "Not recorded"}
						/>
						<Fact
							label="Estimates acknowledged"
							value={request.estimatesAcknowledgedAt ? formatDateTime(request.estimatesAcknowledgedAt) : "Not recorded"}
						/>
						<Fact
							label="Disclosures"
							value={[
								request.recordingDisclosure ? "recording disclosed" : "recording not disclosed",
								request.aiDisclosure ? "AI disclosed" : "AI not disclosed",
							].join(" · ")}
						/>
					</Stack>
				</Grid>
			</Grid>

			<Divider />

			<Box>
				<Typography variant="subtitle2" sx={{ mb: 1.5 }}>
					Timeline
				</Typography>

				{request.events.length === 0 ? (
					<EmptyPanel icon={<ClockCounterClockwiseIcon size={28} />} title="No moves recorded yet" />
				) : (
					<Stack spacing={1}>
						{/* By (at, seq), never by at alone — see `compareEventOrder`. Confirmation and
						    provisioning each write two moves inside one transaction, so they share a
						    timestamp and `at` on its own leaves their order to the query plan. */}
						{request.events.toSorted(compareEventOrder).map((event) => (
							<Stack
								key={event.id}
								direction={{ sm: "row", xs: "column" }}
								spacing={1}
								sx={{ alignItems: { sm: "center" } }}
							>
								<Typography variant="caption" color="text.secondary" sx={{ minWidth: 150 }}>
									{formatDateTime(event.at)}
								</Typography>
								<Typography variant="body2">
									{event.fromState ? `${labelOf(REQUEST_STATE_LABELS, event.fromState)} → ` : ""}
									<strong>{labelOf(REQUEST_STATE_LABELS, event.toState)}</strong>
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{event.actorName ?? "the platform"}
									{event.reason ? ` · ${event.reason}` : ""}
								</Typography>
							</Stack>
						))}
					</Stack>
				)}
			</Box>
		</Stack>
	);
}

/**
 * A request nothing ever priced, and the one button that finds out why.
 *
 * `autoQuote()` swallows every refusal on the way through `/submit` so the client's submit never
 * fails on our housekeeping; the cost is that a request whose rates are not confirmed sits there
 * with nothing anywhere saying so. The refusal is printed verbatim because it names the exact
 * price-book rows that are blocking it — softening that into "could not be priced" costs the one
 * person who can fix it the only thing they needed to know.
 */
function NeverPriced({ request }: { request: CampaignRequestDetail }): React.JSX.Element {
	const navigate = useNavigate();
	const quoteMutation = useQuoteCampaignRequest();
	const [priceError, setPriceError] = React.useState<string | null>(null);

	const priceNow = async (): Promise<void> => {
		setPriceError(null);

		try {
			await quoteMutation.mutateAsync({ id: request.id });
		} catch (error_: any) {
			setPriceError(campaignRequestErrorMessage(error_, "This request could not be priced."));
		}
	};

	return (
		<Alert
			severity="warning"
			action={
				<Stack direction="row" spacing={1}>
					<Button color="inherit" size="small" onClick={() => navigate("/platform/price-book")}>
						Price Book
					</Button>
					<Button
						color="inherit"
						size="small"
						variant="outlined"
						disabled={quoteMutation.isPending}
						onClick={() => void priceNow()}
					>
						Price It Now
					</Button>
				</Stack>
			}
		>
			<AlertTitle>This request has never been priced</AlertTitle>
			<Typography variant="body2">
				{priceError ?? "The usual cause is a platform rate nobody has confirmed. Price it now to see which."}
			</Typography>
		</Alert>
	);
}
